import React, { useState, useRef, useEffect } from 'react';
import { useModalLayer } from '../../lib/modalStack';
import { Sparkles, ChevronDown, Loader } from 'lucide-react';
import { getAuth } from 'firebase/auth';

type ImproveMode = 'shorten' | 'expand' | 'grammar';

const MODE_LABELS: Record<ImproveMode, string> = {
  shorten: 'Shorten',
  expand: 'Expand',
  grammar: 'Improve Grammar',
};

const MODE_PROMPTS: Record<ImproveMode, string> = {
  shorten: 'Shorten the following text while preserving all key information and any {{field}} placeholder tags. Return ONLY the improved text with no explanation.',
  expand: 'Expand on the following text adding more detail and professional depth while preserving any {{field}} placeholder tags. Return ONLY the improved text with no explanation.',
  grammar: 'Fix grammar, spelling, punctuation, and improve clarity while preserving any {{field}} placeholder tags. Return ONLY the improved text with no explanation.',
};

interface ImproveTextDropdownProps {
  /**
   * The current text value to improve.
   */
  getValue: () => string;
  /**
   * Called with the improved text to update the source.
   */
  onImprove: (improved: string) => void;
  /**
   * True if the value is HTML content rather than plain text.
   * When true, the AI is told to return HTML and we sanitize via DOMPurify.
   */
  isHtml?: boolean;
  /**
   * Additional context about what this text is for (e.g. "email body", "job description").
   */
  context?: string;
  /**
   * Optional: the current subject line (for email contexts).
   */
  subject?: string;
  /**
   * Optional key to persist open/closed state (if multiple on one page).
   */
  storageKey?: string;
}

export default function ImproveTextDropdown({
  getValue,
  onImprove,
  isHtml = false,
  context = 'text',
  subject = '',
  storageKey,
}: ImproveTextDropdownProps) {
  const [open, setOpen] = useState(false);
  // SYS-11: layer assigned by the modal stack while open (the z scale
  // has no generated named utilities in Tailwind v4 — overlays register
  // with the stack instead; see index.css).
  const layerZ = useModalLayer(open);
  const [loading, setLoading] = useState<ImproveMode | null>(null);
  const [error, setError] = useState('');
  const errorZ = useModalLayer(!!error);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleImprove = async (mode: ImproveMode) => {
    const value = getValue();
    if (!value.trim() || value === '<br>') {
      setError(`Please write ${context} before improving.`);
      setTimeout(() => setError(''), 3000);
      return;
    }

    setLoading(mode);
    setError('');
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/text/improve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          text: value,
          mode,
          isHtml,
          context,
          subject: subject || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.hint || 'Improve failed');

      let improved = data.improvedText || '';
      if (!improved) throw new Error('No response from AI');

      // If HTML, sanitize; but still preserve field tags
      if (isHtml) {
        try {
          const DOMPurify = (await import('dompurify')).default;
          improved = DOMPurify.sanitize(improved, {
            ALLOWED_TAGS: ['p', 'b', 'i', 'br', 'ul', 'ol', 'li', 'a', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'span', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img', 'hr'],
            ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'target', 'rel', 'style', 'width', 'height'],
          });
        } catch {
          // fall through — DOM purify not available
        }
      }

      onImprove(improved);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to improve text');
      setTimeout(() => setError(''), 4000);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={loading !== null}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-purple-100 text-purple-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed text-[10px] font-bold transition-all"
        title="Improve Text with AI"
      >
        {loading ? (
          <>
            <Loader className="h-3 w-3 animate-spin" />
            <span>{MODE_LABELS[loading] || 'Working...'}</span>
          </>
        ) : (
          <>
            <Sparkles className="h-3 w-3" />
            <span>Improve</span>
            <ChevronDown className="h-2.5 w-2.5" />
          </>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl min-w-[160px] overflow-hidden animate-fade-in" style={{ zIndex: layerZ }}>
          {(Object.keys(MODE_LABELS) as ImproveMode[]).map(mode => (
            <button
              key={mode}
              type="button"
              onClick={() => handleImprove(mode)}
              disabled={loading !== null}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-purple-50 hover:text-purple-700 transition-all flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed border-b border-slate-50 last:border-b-0"
            >
              {loading === mode ? (
                <Loader className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3 text-purple-400" />
              )}
              {MODE_LABELS[mode]}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="absolute right-0 top-full mt-10 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-caption font-bold text-red-600 shadow-lg whitespace-nowrap animate-fade-in" style={{ zIndex: errorZ }}>
          {error}
        </div>
      )}
    </div>
  );
}
